import { useState, type ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import {
  CircleHelp,
  FilePlus2,
  LayoutGrid,
  LogOut,
  Menu,
  Search,
  Settings,
  Users,
  X,
} from 'lucide-react'
import { Wordmark } from './Logo'
import { usePortal } from '../lib/portal-context'
import { avatarUrl } from '../lib/api'
import { cx } from '../lib/format'

export function AppShell({
  children,
  search,
  onSearch,
  actions,
}: {
  children: ReactNode
  search?: string
  onSearch?: (value: string) => void
  actions?: ReactNode
}) {
  const { config, user, can, logout } = usePortal()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)

  const nav = [
    { to: '/', label: 'Cases', icon: LayoutGrid, end: true, show: can('cases:read') },
    { to: '/new', label: 'New report', icon: FilePlus2, show: can('cases:write') },
    { to: '/users', label: 'Users', icon: Users, show: can('users:manage') },
    { to: '/settings', label: 'Settings', icon: Settings, show: true },
    { to: '/help', label: 'How it works', icon: CircleHelp, show: true },
  ].filter((item) => item.show)

  const photo = avatarUrl(user?.avatarFile)
  const roleLabel = config?.roles.find((role) => role.code === user?.role)?.label ?? user?.role

  return (
    <div className="flex min-h-screen bg-ink-950">
      <aside
        className={cx(
          'no-print fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-white/[0.06] bg-ink-900/95 backdrop-blur-xl transition-transform duration-200 lg:translate-x-0',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between px-5">
          <Link to="/" className="flex items-center" onClick={() => setMenuOpen(false)}>
            <Wordmark height={22} />
          </Link>
          <button
            type="button"
            className="btn-subtle -mr-2 p-2 lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-3 pt-2">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition',
                  isActive
                    ? 'bg-white/[0.07] text-white shadow-[inset_2px_0_0_0_theme(colors.altair.500)]'
                    : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200',
                )
              }
            >
              <Icon size={17} strokeWidth={1.9} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/[0.06] p-4">
          {user ? (
            <>
              <div className="mb-2 flex items-center gap-2.5 rounded-lg bg-white/[0.02] px-3 py-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/[0.06] text-[11px] font-semibold text-slate-300">
                  {photo ? (
                    <img src={photo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    user.name
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((part) => part[0]?.toUpperCase() ?? '')
                      .join('')
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-semibold text-white">{user.name}</div>
                  <div className="truncate text-[11px] text-slate-500">{roleLabel}</div>
                </div>
              </div>
              <button
                type="button"
                className="btn-subtle w-full justify-start px-3 text-[12.5px]"
                onClick={async () => {
                  await logout()
                  navigate('/')
                }}
              >
                <LogOut size={14} />
                Sign out
              </button>
            </>
          ) : null}
        </div>
      </aside>

      {menuOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          className="no-print fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-[248px]">
        <header className="no-print sticky top-0 z-20 flex h-16 min-w-0 items-center gap-3 border-b border-white/[0.06] bg-ink-950/85 px-4 backdrop-blur-xl sm:px-6">
          <button
            type="button"
            className="btn-subtle p-2 lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={19} />
          </button>

          {onSearch ? (
            <div className="relative max-w-md flex-1">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                value={search ?? ''}
                onChange={(event) => onSearch(event.target.value)}
                placeholder="Search plate, street, case number…"
                className="input input-sm py-2 pl-9"
              />
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <div className="flex items-center gap-2">{actions}</div>
        </header>

        <main className="min-w-0 flex-1 grid-bg">{children}</main>
      </div>
    </div>
  )
}

export function PageHeader({
  eyebrow,
  title,
  blurb,
  actions,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  blurb?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow ? (
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-altair-400">
            {eyebrow}
          </div>
        ) : null}
        <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-white">{title}</h1>
        {blurb ? (
          <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-slate-400">{blurb}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}
